import {
  createFormHook,
  createFormHookContexts,
  useStore,
} from "@tanstack/react-form";
import { Check } from "lucide-react";
import { Input } from "./ui/input";
import { useContext, useId, type ComponentProps, type ReactNode } from "react";
import { FormItem, FormItemContext } from "./ui/form";
import { cn } from "@/lib/utils";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import { PhoneNumberInput } from "./phone-number-input";

const { fieldContext, formContext, useFieldContext, useFormContext } =
  createFormHookContexts();

export const { useAppForm, withForm, withFieldGroup } = createFormHook({
  fieldComponents: {
    TextField,
    PhoneField,
    CheckboxField,
    SwitchField,
    RadioGroupField,
  },
  formComponents: {
    FormDebugger,
    SubmitButton,
  },
  formContext,
  fieldContext,
});

function useHasError() {
  const field = useFieldContext<string>();

  const errors = useStore(field.store, (state) => state.meta.errors);
  const isSubmitted = useStore(
    field.form.store,
    (state) => state.submissionAttempts > 0,
  );
  return (
    (isSubmitted || field.state.meta.isDirty || field.state.meta.isTouched) &&
    errors?.[0]
  );
}

function FormDebugger() {
  const form = useFormContext();
  return (
    <form.Subscribe selector={(state) => state.values}>
      {(values) => <pre>{JSON.stringify(values, null, 2)}</pre>}
    </form.Subscribe>
  );
}

function TextField({
  label,
  className,
  ...inputProps
}: { label: ReactNode } & Omit<
  ComponentProps<typeof Input>,
  "value" | "onChange"
>) {
  const field = useFieldContext<string>();
  const id = useId();
  const hasError = useHasError();

  return (
    <FormItem className={className}>
      <FormLabel error={hasError} htmlFor={id}>
        {label}
      </FormLabel>
      <div className="flex items-center">
        <Input
          {...inputProps}
          id={id}
          className={cn(hasError && "border-red-500")}
          value={field.state.value}
          onBlur={(e) => {
            field.handleBlur();
            inputProps.onBlur?.(e);
          }}
          onChange={(value) => field.handleChange(value)}
          onEnterSubmit={() => field.form.handleSubmit()}
        />
      </div>
      <FormMessage />
    </FormItem>
  );
}

function PhoneField({
  label,
  className,
  tabIndex,
}: {
  label: ReactNode;
  className?: string;
  tabIndex: number;
}) {
  const field = useFieldContext<string>();
  const id = useId();
  const hasError = useHasError();

  return (
    <FormItem className={className}>
      <FormLabel error={hasError} htmlFor={id}>
        {label}
      </FormLabel>
      <PhoneNumberInput
        id={id}
        tabIndex={tabIndex}
        value={field.state.value ?? ""}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        onEnterSubmit={() => field.form.handleSubmit()}
        hasError={!!hasError}
      />
      <FormMessage />
    </FormItem>
  );
}

function CheckboxField({
  label,
  labelClassname,
  className,
  ...inputProps
}: { label: ReactNode; labelClassname?: string } & Omit<
  ComponentProps<typeof Input>,
  | "value"
  | "onChange"
  | "onBlur"
  | "type"
  | "checked"
  | "tabIndex"
  | "onEnterSubmit"
  | "size"
>) {
  const field = useFieldContext<boolean>();
  const id = useId();

  return (
    <FormItem className={className}>
      <div className="flex items-center space-x-2">
        <Checkbox
          {...inputProps}
          id={id}
          checked={field.state.value || false}
          onChange={(e) => field.handleChange(e.target.checked)}
        />
        <Label className={labelClassname} htmlFor={id}>
          {label}
        </Label>
      </div>
    </FormItem>
  );
}

function SwitchField({
  label,
  className,
}: {
  label: ReactNode;
  className?: string;
}) {
  const field = useFieldContext<boolean>();

  return (
    <FormItem
      className={className}
      onClick={() => field.handleChange(!field.state.value)}
    >
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={field.state.value || false}
          className="w-4 h-4"
        />
        <Label>{label}</Label>
      </div>
    </FormItem>
  );
}

export function FormMessage({
  className,
  ...props
}: React.ComponentProps<"p">) {
  const field = useFieldContext();
  const isSubmitted = useStore(
    field.form.store,
    (state) => state.submissionAttempts > 0,
  );

  return (
    <FormError
      errors={
        isSubmitted || field.state.meta.isDirty || field.state.meta.isTouched
          ? field.state.meta.errors
          : []
      }
      className={className}
      {...props}
    />
  );
}

export function FormError({
  className,
  errors,
  ...props
}: React.ComponentProps<"p"> & { errors: any[] }) {
  const error = errors?.[0];
  const body = error && error.message ? String(error?.message) : props.children;

  if (!body) {
    return null;
  }

  return (
    <p className={cn("text-destructive text-sm", className)} {...props}>
      {body}
    </p>
  );
}

export function FormLabel({
  className,
  error,
  ...props
}: React.ComponentProps<typeof Label> & { error?: string | boolean }) {
  const { id } = useContext(FormItemContext);

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn("data-[error=true]:text-destructive ", className)}
      htmlFor={id}
      {...props}
    />
  );
}

export function CheckboxFormItem({
  label,
  checked,
  onCheckedChange,
}: {
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="w-4 h-4"
      />
      <Label>{label}</Label>
    </div>
  );
}

function SubmitButton({
  children,
  ...props
}: {
  children: ReactNode;
} & ComponentProps<"button">) {
  const form = useFormContext();
  return (
    <Button {...props} onClick={() => form.handleSubmit()}>
      {children}
    </Button>
  );
}

function RadioGroupField<T extends string>({
  label,
  className,
  options,
  onChange,
  variant = "default",
  ...inputProps
}: {
  label: string | ReactNode;
  options: { label: string | ReactNode; value: T }[];
  onChange?: (value: T) => void;
  variant?: "default" | "buttons";
} & Omit<
  ComponentProps<typeof RadioGroup>,
  "value" | "onValueChange" | "onBlur" | "children" | "onChange"
>) {
  const field = useFieldContext<string>();

  // Large, touch-friendly tap targets for the kiosk. Each option is a full
  // button rather than a small radio dot, so it is easy to hit on a touch screen.
  if (variant === "buttons") {
    return (
      <FormItem className={cn("gap-3", className)}>
        {label && (
          <FormLabel
            error={field.state.meta.errors?.[0]}
            className="text-[26px] font-semibold text-bub-dark-blue"
          >
            {label}
          </FormLabel>
        )}
        <div className="flex gap-4" role="radiogroup">
          {options.map(({ label, value }) => {
            const selected = field.state.value === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  onChange?.(value);
                  field.handleChange(value);
                }}
                className={cn(
                  "relative flex flex-1 items-center justify-center rounded-2xl border-2 px-22 py-6 text-[32px] font-medium shadow-md transition-all duration-150 active:scale-[0.98]",
                  selected
                    ? "border-bub-dark-blue bg-bub-dark-blue text-white"
                    : "border-gray-200 bg-white text-gray-600 hover:border-bub-dark-blue/40",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-5 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-sm transition-all duration-200",
                    selected
                      ? "scale-100 opacity-100"
                      : "pointer-events-none scale-50 opacity-0",
                  )}
                >
                  <Check
                    className="size-7 text-bub-dark-blue"
                    strokeWidth={3.5}
                  />
                </span>
                {label}
              </button>
            );
          })}
        </div>
        <FormMessage />
      </FormItem>
    );
  }

  return (
    <FormItem className={className}>
      {label && (
        <FormLabel error={field.state.meta.errors?.[0]}>{label}</FormLabel>
      )}
      <RadioGroup
        {...inputProps}
        value={field.state.value}
        onValueChange={(value) => {
          onChange?.(value as T);
          field.handleChange(value);
        }}
      >
        <div className="flex space-x-8">
          {options.map(({ label, value }) => (
            <div className="flex items-center space-x-2">
              <RadioGroupItem value={value} id={value} />
              <Label
                htmlFor={value}
                className="text-gray-700 font-normal text-base cursor-pointer"
              >
                {label}
              </Label>
            </div>
          ))}
        </div>
      </RadioGroup>
      <FormMessage />
    </FormItem>
  );
}
